import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Select from 'react-select';
import axios from 'axios';
import AsyncSelect from 'react-select/async';
import ReactHtmlParser from 'react-html-parser';
import MultiSelectCreatableTextInput from '../MultiSelectCreatableTextInput';
import { isMobile } from '../../../screenUtils';
import { getValuesContaining, getValuesWithout, filterOutDuplicateObjects } from '../../../arrayUtils';
import DropdownIndicator from '../DropdownIndicator';
import dropdownStyleUtils from '../../../dropdownStyleUtils';
import './MultiSelectInput.scss';
import { analyticsTrack } from '../../../analyticsUtils';

const components = {
  DropdownIndicator,
  IndicatorSeparator: () => null,
};

const reorderSeniorityOption = (options, mobile) => {
  // re-order the Seniory options to be display appropriately
  const order = ['Associate', 'Vice President', 'Manager', 'C-Level', 'Director'];
  const orderMobile = ['Associate', 'Manager', 'Director', 'Vice President', 'C-Level'];

  const reOrderedOption = [];
  if (mobile) {
    orderMobile.forEach((label) => {
      const found = options.find((el) => el.label === label);
      if (found) reOrderedOption.push(found);
    });
  } else {
    order.forEach((label) => {
      const found = options.find((el) => el.label === label);
      if (found) reOrderedOption.push(found);
    });
  }

  return reOrderedOption;
};

class MultiSelectInput extends Component {
  constructor(props) {
    super(props);
    this.customValues = getValuesContaining(props.values, 'custom:');
    this.nonCustomValues = getValuesWithout(props.values, 'custom:');
    this.state = {
      otherSelected: this.customValues.length > 0,
      menuIsOpen: props.menuIsOpen,
      showLimitReached: false,
      usedOptions: props.options,
    };
    this.withinLimit = this.withinLimit.bind(this);
    this.toggleOtherSelected = this.toggleOtherSelected.bind(this);
    this.getOptions = this.getOptions.bind(this);
    this.handleFilterChange = this.handleFilterChange.bind(this);
    this.handleCustomSelectChange = this.handleCustomSelectChange.bind(this);
    this.handleNonCustomSelectChange = this.handleNonCustomSelectChange.bind(this);
    this.handleAllSelect = this.handleAllSelect.bind(this);
    this.handleToggleMenu = this.handleToggleMenu.bind(this);
  }

  getOptions(input) {
    const { asyncUrl } = this.props;
    return axios.get(
      asyncUrl,
      { params: { term: input } },
    ).then((response) => {
      const compare = response.data.filter((i) => i.label.toLowerCase().includes(input.toLowerCase()));

      return compare.map((i) => ({
        label: i.label,
        value: i.value,
      }));
    });
  }

  withinLimit(values) {
    const { limit } = this.props;
    return (limit === null
      || (values.length <= limit || this.nonCustomValues.length > values.length));
  }

  toggleOtherSelected() {
    const { otherSelected } = this.state;
    const {
      questionKey, onboardingQuestionnaire, userId, text,
    } = this.props;

    // If Other is currently selected and we are toggling it to
    // be unselected, then we should clear the custom values.
    if (otherSelected === true) {
      analyticsTrack(`Toggling Other - ${text}`, {
        location: onboardingQuestionnaire ? 'Onboarding Flow' : 'Not Onboarding Flow',
        questionKey,
        action: 'Enabled',
        userId,
      });
      this.customValues = [];
      if (!this.isLimitReached()) {
        this.setState({ showLimitReached: false });
      }
    } else {
      analyticsTrack(`Toggling Other - ${text}`, {
        location: onboardingQuestionnaire ? 'Onboarding Flow' : 'Not Onboarding Flow',
        questionKey,
        action: 'Disabled',
        userId,
      });
      if (this.isLimitReached()) {
        this.setState({ showLimitReached: true });
        return;
      }
    }

    this.handleAllSelect();
    this.setState({ otherSelected: !otherSelected });
  }

  handleToggleMenu() {
    const { closeOnSelect } = this.props;

    if (this.props.menuIsOpen && !closeOnSelect) return;

    this.setState({ menuIsOpen: !this.state.menuIsOpen });
  }

  handleAllSelect() {
    const { handleSelectChange } = this.props;
    handleSelectChange([...this.nonCustomValues, ...this.customValues]);
  }

  handleNonCustomSelectChange(values) {
    const {
      questionKey, text, onboardingQuestionnaire, userId,
    } = this.props;

    if (Array.isArray(values)) {
      if (this.withinLimit(values)) {
        this.nonCustomValues = values;
      }
    } else {
      const index = this.nonCustomValues.findIndex((x) => x.value === values.value);
      let action;

      if (index !== -1) {
        this.nonCustomValues.splice(index, 1);
        action = 'Removed';

        if (!this.isLimitReached()) {
          this.setState({ showLimitReached: false });
        }
      } else {
        action = 'Added';

        if (this.isLimitReached()) {
          analyticsTrack(`Attempting to add more - ${text}`, {
            location: onboardingQuestionnaire ? 'Onboarding Flow' : 'Not Onboarding Flow',
            value: values.label,
            questionKey,
            action,
            userId,
          });

          this.setState({ showLimitReached: true });
          return;
        }

        this.nonCustomValues.push(values);
      }
      analyticsTrack(`Indicating ${text}`, {
        location: onboardingQuestionnaire ? 'Onboarding Flow' : 'Not Onboarding Flow',
        value: values.label,
        questionKey,
        action,
        userId,
      });
    }
    this.handleAllSelect();
  }

  handleCustomSelectChange(values, action) {
    const {
      questionKey, text, onboardingQuestionnaire, userId,
    } = this.props;
    let newValues;
    if (action === 'clear') {
      newValues = [];
    } else if (action === 'delete-value-key') {
      newValues = [...values].slice(0, -1);
    } else if (action === 'remove-value') {
      newValues = [...values];
    } else if (!this.isLimitReached()) {
      const deduped = filterOutDuplicateObjects(this.customValues, values.value);
      newValues = [...deduped, values];
    }
    analyticsTrack(`Action on Other - ${text}`, {
      location: onboardingQuestionnaire ? 'Onboarding Flow' : 'Not Onboarding Flow',
      value: values.label,
      questionKey,
      action,
      userId,
    });
    const { onlyCustom } = this.props;
    if (!onlyCustom || (onlyCustom && this.withinLimit(newValues))) {
      this.customValues = newValues;
      this.handleAllSelect();
    }
  }

  handleFilterChange(value) {
    let { usedOptions } = this.state;
    const { options } = this.props;

    usedOptions = options.filter((opt) => opt.label.toLowerCase().includes(value.toLowerCase()));
    this.setState({ usedOptions });
  }

  isLimitReached() {
    const { limit } = this.props;
    return limit ? (this.nonCustomValues.length + this.customValues.length) >= limit : false;
  }

  renderLimitWarning() {
    const { limit } = this.props;

    if (limit && this.isLimitReached()) {
      return (
        <div className="question-warning body-text-2 mb-2">{`You have selected ${limit} options. You cannot select another one.`}</div>
      );
    }
    return null;
  }

  renderCreatableTextInput() {
    const { hasOther, onlyCustom, disabled } = this.props;
    const { otherSelected } = this.state;
    if ((hasOther && otherSelected) || onlyCustom) {
      return (
        <MultiSelectCreatableTextInput
          values={this.customValues}
          handleCustomSelectChange={this.handleCustomSelectChange}
          isDisabled={disabled}
          enterPrompt={onlyCustom}
        />
      );
    }

    return null;
  }

  renderSelect() {
    const {
      asyncUrl,
      disabled,
      onlyCustom,
      suggestedAnswers,
      optionType,
      placeholder,
      closeOnSelect,
    } = this.props;
    let { options } = this.props;
    const { menuIsOpen } = this.state;
    if (suggestedAnswers && asyncUrl) {
      options = suggestedAnswers.map((s) => ({ value: s.id, label: s.name }));
    } else if (suggestedAnswers) {
      const names = suggestedAnswers.map((s) => s.name);
      const suggestedOptions = options.filter((option) => (names.includes(option.label)));
      const filteredOptions = options.filter((option) => (!names.includes(option.label)));
      options = [{ label: 'Suggested for you', options: suggestedOptions }].concat([{ label: `All ${optionType}`, options: filteredOptions }]);
    }
    if (onlyCustom) { return null; }

    if (asyncUrl) {
      return (
        <AsyncSelect
          components={components}
          styles={{ ...dropdownStyleUtils }}
          loadOptions={this.getOptions}
          defaultOptions={options}
          value={this.nonCustomValues}
          maxMenuHeight={500}
          onChange={(e) => {
            this.handleNonCustomSelectChange(e); this.setState({ menuIsOpen: !closeOnSelect });
          }}
          isDisabled={disabled}
          closeOnSelect={false}
          closeMenuOnSelect={closeOnSelect}
          onSelectResetsInput={false}
          classNamePrefix="clora-multiselect"
          placeholder={placeholder}
          isMulti
          menuIsOpen={menuIsOpen}
          cacheOptions
        />
      );
    }

    return (
      <Select
        components={components}
        styles={{ ...dropdownStyleUtils }}
        options={options}
        value={this.nonCustomValues}
        maxMenuHeight={450}
        onChange={(e) => {
          this.handleNonCustomSelectChange(e);
          this.setState({ menuIsOpen: closeOnSelect ? false : menuIsOpen });
        }}
        isDisabled={disabled}
        classNamePrefix="clora-multiselect"
        placeholder={placeholder}
        isMulti
        menuIsOpen={menuIsOpen}
        onMenuOpen={this.handleToggleMenu}
        onMenuClose={closeOnSelect ? () => {} : this.handleToggleMenu}
        menuShouldScrollIntoView={false}
      />
    );
  }

  renderCheckboxMultiSelect() {
    const {
      options, subtext, hasOther, suggestedAnswers,
      marginTop, questionKey, onboardingQuestionnaire,
    } = this.props;
    const { otherSelected } = this.state;
    const subtextStyle = onboardingQuestionnaire ? 'subtext' : '';
    const newOptions = questionKey === 'role_level' ? reorderSeniorityOption(options, isMobile()) : options;
    return (
      <div className={questionKey === 'role_level' ? 'checkbox-multiselect-with-filter' : ''}>
        {this.renderLimitWarning()}
        { subtext && <div className={`body-text-2 mb-${marginTop} ${subtextStyle}`}>{subtext}</div> }
        <div className="checkbox-multi-select">
          { newOptions.map((item) => (
            <label
              className={`custom-checkbox-container ${suggestedAnswers && suggestedAnswers.includes(item.label) && 'suggested'}`}
              htmlFor={item.value}
              key={item.label}
            >
              <input
                type="checkbox"
                id={item.value}
                checked={this.nonCustomValues.findIndex((x) => x.value === item.value) !== -1}
                onChange={() => this.handleNonCustomSelectChange(item)}
              />
              <span className="checkmark" />
              <span className="label body-text-2">{item.label}</span>
              {suggestedAnswers
                && suggestedAnswers.includes(item.label)
                && <span className="suggested-text">Suggested for you</span>}
            </label>
          ))}
        </div>

        { hasOther
          && (
          <div className="align-horizontally-parent">
            <div className="align-horizontally-child">
              <label className="custom-checkbox-container body-text-2" htmlFor="other">
                <input
                  type="checkbox"
                  id="other"
                  checked={otherSelected}
                  onChange={() => this.toggleOtherSelected({ label: 'Other' })}
                />
                <span className="checkmark" />
                <span className="label">Other</span>
              </label>
            </div>

            <div className="align-horizontally-child ms-3">
              <MultiSelectCreatableTextInput
                values={this.customValues}
                handleCustomSelectChange={this.handleCustomSelectChange}
              />
            </div>

          </div>
          )}
      </div>
    );
  }

  renderSelectedItems() {
    if (this.nonCustomValues.length === 0 && this.customValues.length === 0) {
      return null;
    }

    return (
      <div className="tag-selection-set">
        <div className="non-custom-values">
          { this.nonCustomValues.map((opt, idx) => (
            <div className="tag-selection non-custom-values">
              <label htmlFor={`selected-item-${idx}`}>{opt.label}</label>
              <i
                role="menuitem"
                id={`selected-item-${idx}`}
                className="fa fa-times"
                onClick={() => this.handleNonCustomSelectChange(opt)}
                tabIndex="0"
              />
            </div>
          )) }
        </div>
        <div className="custom-values">
          { this.customValues.map((opt, idx) => (
            <div className="tag-selection">
              <label htmlFor={`selected-custom-item-${idx}`}>{opt.label}</label>
              <i
                role="menuitem"
                id={`selected-custom-item-${idx}`}
                className="fa fa-times"
                onClick={() => this.handleCustomSelectChange(this.customValues.filter((v) => v.label !== opt.label), 'remove-value')}
                tabIndex="0"
              />
            </div>
          )) }
        </div>
      </div>
    );
  }

  renderCheckboxMultiSelectWithFilter() {
    const {
      questionKey, subtext, hasOther, suggestedAnswers,
      marginTop, hasFilter, text,
    } = this.props;
    const { usedOptions, otherSelected, showLimitReached } = this.state;
    const isDisabled = !!this.isLimitReached();
    const options = questionKey === 'role_level' ? reorderSeniorityOption(usedOptions, isMobile()) : usedOptions;
    return (
      <div className="checkbox-multiselect-with-filter">
        { showLimitReached && this.renderLimitWarning()}
        { subtext && <div className={`body-text-2 mb-${marginTop}`}>{ReactHtmlParser(subtext)}</div> }
        {this.renderSelectedItems()}
        { hasFilter && (
        <div className="filter">
          <div className="input-field">
            <input
              className="input-base border-radius-small"
              type="text"
              name="role"
              placeholder={`Filter ${text.toLowerCase()}`}
              onChange={(e) => this.handleFilterChange(e.target.value)}
            />
          </div>
        </div>
        )}
        <div className="checkbox-multi-select">
          { options.map((item) => {
            const isChecked = this.nonCustomValues.findIndex((x) => x.value === item.value) !== -1;

            return (
              <label
                className={`
                  custom-checkbox-container ${suggestedAnswers && suggestedAnswers.includes(item.label) && 'suggested'}
                  ${isDisabled && !isChecked ? 'disabled' : ''}
                `}
                htmlFor={`${item.label.replace(/\s+/g, '-').toLowerCase()}-${item.value}`}
                key={item.label}
              >
                <input
                  type="checkbox"
                  id={`${item.label.replace(/\s+/g, '-').toLowerCase()}-${item.value}`}
                  checked={isChecked}
                  onChange={() => this.handleNonCustomSelectChange(item)}
                />
                <span className="checkmark" />
                <span className="label body-text-2">{item.label}</span>
                {suggestedAnswers
                  && suggestedAnswers.includes(item.label)
                  && <span className="suggested-text">Suggested for you</span>}
              </label>
            );
          })}
        </div>
        { (hasOther && questionKey === 'skills')
          && (
          <div className="other-wrapper">
            <label
              className={`
                custom-checkbox-container body-text-2
                ${isDisabled && !otherSelected ? 'disabled' : ''}
              `}
              htmlFor={`other-${questionKey}`}
              key={`other-${questionKey}`}
            >
              <input
                type="checkbox"
                id={`other-${questionKey}`}
                checked={otherSelected}
                onChange={() => this.toggleOtherSelected({ label: 'Other' })}
                disabled={isDisabled}
              />
              <span className="checkmark" />
              <span className="label">Other</span>
            </label>
            <MultiSelectCreatableTextInput
              values={this.customValues}
              handleCustomSelectChange={this.handleCustomSelectChange}
              showDropdownIndicator={false}
            />
          </div>
          )}
      </div>
    );
  }

  renderDropDownMultiSelect() {
    const {
      subtext, hasOther, onlyCustom, disabled, marginTop, questionKey,
    } = this.props;
    return (
      <div className="multi-select">
        {this.renderLimitWarning()}
        <div className={`body-text-2 mb-${marginTop} ${disabled ? 'disabled-grey' : ''}`}>
          { ReactHtmlParser(subtext) }
          {onlyCustom
            && <div><strong>Press Enter after each item.</strong></div>}
          { (hasOther && !onlyCustom && questionKey === 'tools')
            && (
            <div className="mt-5">
              <span className="medium-weight">Don&#39;t see your choice? </span>
              Click&nbsp;
              <span
                role="button"
                className="highlight-text pointer-underline-text"
                onClick={disabled ? () => {} : this.toggleOtherSelected}
                tabIndex={0}
              >
                here
              </span>
              &nbsp;to add it.
            </div>
            )}
        </div>
        { this.renderCreatableTextInput() }
        { this.renderSelect() }
      </div>
    );
  }

  render() {
    const { style } = this.props;
    if (style === 'multiselect') {
      return this.renderDropDownMultiSelect();
    }
    if (style === 'checkbox_multiselect_filter') {
      return this.renderCheckboxMultiSelectWithFilter();
    }

    return this.renderCheckboxMultiSelect();
  }
}

MultiSelectInput.propTypes = {
  questionKey: PropTypes.string.isRequired,
  subtext: PropTypes.string,
  disabled: PropTypes.bool,
  options: PropTypes.arrayOf(PropTypes.shape({})),
  handleSelectChange: PropTypes.func.isRequired,
  asyncUrl: PropTypes.string,
  values: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  hasOther: PropTypes.bool.isRequired,
  hasFilter: PropTypes.bool.isRequired,
  style: PropTypes.string,
  limit: PropTypes.number,
  onlyCustom: PropTypes.bool,
  suggestedAnswers: PropTypes.shape([]),
  optionType: PropTypes.string,
  placeholder: PropTypes.string,
  menuIsOpen: PropTypes.bool,
  marginTop: PropTypes.string,
  userId: PropTypes.number.isRequired,
  text: PropTypes.string.isRequired,
  onboardingQuestionnaire: PropTypes.bool,
  closeOnSelect: PropTypes.bool,
};

MultiSelectInput.defaultProps = {
  subtext: '',
  disabled: false,
  options: [],
  asyncUrl: null,
  style: 'multiselect',
  limit: null,
  onlyCustom: false,
  suggestedAnswers: null,
  optionType: '',
  placeholder: 'Select or type...',
  menuIsOpen: true,
  marginTop: '20',
  onboardingQuestionnaire: false,
  closeOnSelect: false,
};

export default MultiSelectInput;
